import type { BlobBackend } from './backend';

/**
 * Настоящие файлы в приватном хранилище приложения (OPFS).
 *
 * Это тот же способ, что у Android: документ — файл на диске, а не запись в базе.
 * Файл целиком в память не читается — `Blob`, который отдаёт `getFile()`, читается
 * по мере надобности, и том дела на триста страниц не поднимает всю память разом.
 */
export function opfsBackend(root: FileSystemDirectoryHandle): BlobBackend {
  /** Идём по пути, создавая папки при записи и молча возвращая `null` при чтении. */
  const walk = async (path: string, create: boolean) => {
    const parts = path.split('/');
    const name = parts.pop();
    if (!name) throw new Error(`пустой путь: ${path}`);

    let dir = root;
    for (const part of parts) {
      try {
        dir = await dir.getDirectoryHandle(part, { create });
      } catch {
        return null;
      }
    }
    return { dir, name };
  };

  return {
    kind: 'opfs',

    async write(path, blob) {
      const place = await walk(path, true);
      if (!place) throw new Error(`не удалось создать папку для ${path}`);

      const file = await place.dir.getFileHandle(place.name, { create: true });
      const stream = await file.createWritable();
      await stream.write(blob);
      await stream.close();
    },

    async read(path) {
      const place = await walk(path, false);
      if (!place) return null;

      try {
        const handle = await place.dir.getFileHandle(place.name);
        return await handle.getFile();
      } catch {
        return null;
      }
    },

    async remove(path) {
      const place = await walk(path, false);
      if (!place) return;

      try {
        await place.dir.removeEntry(place.name);
      } catch {
        // Файла уже нет — это и требовалось.
      }
    },

    async list(prefix) {
      const found: { path: string; sizeBytes: number }[] = [];

      const scan = async (dir: FileSystemDirectoryHandle, path: string) => {
        for await (const [name, handle] of dir.entries()) {
          const full = path ? `${path}/${name}` : name;

          if (handle.kind === 'directory') {
            // Заходим только туда, что может дать нужный префикс.
            if (prefix.startsWith(full) || full.startsWith(prefix)) {
              await scan(handle as FileSystemDirectoryHandle, full);
            }
            continue;
          }
          if (full.startsWith(prefix)) {
            const file = await (handle as FileSystemFileHandle).getFile();
            found.push({ path: full, sizeBytes: file.size });
          }
        }
      };

      await scan(root, '');
      return found;
    },
  };
}
